import { Close, ShoppingBag } from '@mui/icons-material'
import { Badge, Box, Button, Divider, Drawer, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { DisplayCurrency, ProductCard, ConfirmationDialog } from 'components'
import { useCart } from 'core'
import { Fragment, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageURLs } from 'Routes'

const CartButton = () => {
  const { cart, totalPrice, quantity, resetCart } = useCart()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isOpen, setIsOpen] = useState(false)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

  const handleEmptyCartClick = (event) => {
    event.stopPropagation()
    setConfirmDialogOpen(true)
  }

  const handleConfirmEmptyCart = () => {
    resetCart()
    setConfirmDialogOpen(false)
  }

  const toggleDrawer = () => {
    setIsOpen((prev) => !prev)
  }

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const handleNavigation = (path) => {
    if (path === location.pathname) {
      setIsOpen(false)
      return
    }
    navigate(path)
  }

  return (
    <div>
      <Tooltip title="Cart">
        <IconButton
          onClick={toggleDrawer}
          size="small"
          aria-label="Cart"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            borderRadius: '50%',
            width: 40,
            height: 40,
            transition: 'all 0.2s ease',
            '&:hover': {
              background: `${theme.palette.primary.main}18`,
              color: theme.palette.primary.main,
            },
          })}
        >
          <Badge badgeContent={quantity} color="error" max={9}>
            <ShoppingBag sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={isOpen}
        onClose={toggleDrawer}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: { xs: '100%', sm: 400 },
            maxWidth: { xs: '100%', sm: 400 },
            height: { xs: '82vh', sm: '100%' },
            borderTopLeftRadius: { xs: 20, sm: 0 },
            borderTopRightRadius: { xs: 20, sm: 0 },
          },
        }}
      >
        <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }} onClick={toggleDrawer}>
          <Close />
        </IconButton>
        <Box display="flex" alignItems="center" height={60} p={2} pr={6}>
          <ShoppingBag color="secondary" />
          <Typography variant="body1" color="secondary" sx={{ ml: 1 }}>
            {quantity} {quantity === 1 ? 'item' : 'items'}
          </Typography>
        </Box>
        <Divider />
        <Box
          sx={{
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {cart.map((product) => (
            <Fragment key={product.itemId}>
              <ProductCard product={product} inCartDrawer />
            </Fragment>
          ))}
        </Box>
        <Box
          sx={{
            p: 2,
            pb: `calc(16px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <Button
            disabled={quantity === 0}
            color="primary"
            variant="contained"
            fullWidth
            sx={{ mb: 1 }}
            onClick={() => handleNavigation(PageURLs.Checkout)}
          >
            Checkout (<DisplayCurrency number={totalPrice} />)
          </Button>
          <Button
            disabled={quantity === 0}
            color="primary"
            variant="outlined"
            fullWidth
            sx={{ mb: 1 }}
            onClick={() => handleNavigation(PageURLs.Cart)}
          >
            View cart
          </Button>
          <Button disabled={quantity < 1} color="secondary" variant="outlined" fullWidth onClick={handleEmptyCartClick}>
            Empty cart
          </Button>
        </Box>
      </Drawer>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmEmptyCart}
        title="Empty Cart"
        description="Are you sure you want to empty your cart? This action cannot be undone."
        confirmText="Empty Cart"
        cancelText="Cancel"
      />
    </div>
  )
}

export default CartButton
